import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import logoOnly from '@/assets/images/logo/csc_logo_logo_only.svg';
import { moduleBadge, moduleHrefs, type MenuEntry } from '../lib/menu';
import { resolveLitHref } from '../lib/navRouteMatchers';
import { useVisibleMenu } from '../hooks/useVisibleMenu';
import { useShellStore } from '../store/shellStore';

/**
 * The permanent 68px module rail (M1 · 86d44z1g9).
 *
 * Owner decision 2026-08-26 (4): a DISABLED module still gets its rail button and still navigates —
 * the user lands on `ModuleGuard`'s inline notice, exactly as they do today. So this component
 * deliberately does NOT consult `useSystemModuleStore`. Document Capture ships `IsEnabled = false`
 * and will dead-end on day one; that is accepted and matches current behaviour.
 */

interface ModuleRailProps {
  /** M6 (86d44z2b7) drops the notification bell in here. */
  bellSlot?: ReactNode;
  /** M7 (86d44z2ff) drops the avatar / profile menu in here. */
  profileSlot?: ReactNode;
}

export function ModuleRail({ bellSlot, profileSlot }: ModuleRailProps) {
  const { pathname } = useLocation();
  const visibleMenu = useVisibleMenu();
  const panelOpen = useShellStore((s) => s.panelOpen);
  const activeModule = useShellStore((s) => s.activeModule);
  const pickModule = useShellStore((s) => s.pickModule);
  const pickLeaf = useShellStore((s) => s.pickLeaf);

  // The nav row this route lights. NOT `pathname.startsWith(href)` — see navRouteMatchers.ts for
  // the three separate ways that is wrong here.
  const litHref = resolveLitHref(pathname);

  return (
    <nav
      aria-label="Modules"
      // Industrial re-bases the generic tokens for this subtree via this attribute. It is a
      // styling contract, not decoration — see the [data-surface='sidebar'] block in globals.css.
      data-surface="sidebar"
      className="flex w-rail flex-shrink-0 flex-col items-center gap-0.5 overflow-y-auto border-r border-sidebar-border bg-sidebar py-2.5"
    >
      {/* Brand mark. Capitol Red is a §3 brand-fixed exception — it does not follow the theme. */}
      <img src={logoOnly} alt="Capitol Steel" className="mb-2 h-7 w-7 flex-shrink-0" />

      {visibleMenu.map((entry) => (
        <RailButton
          key={entry.id}
          entry={entry}
          litHref={litHref}
          panelOpen={panelOpen}
          activeModule={activeModule}
          onPickModule={pickModule}
          onPickLeaf={pickLeaf}
        />
      ))}

      <div className="flex-1" />

      {bellSlot}
      {profileSlot}
    </nav>
  );
}

interface RailButtonProps {
  entry: MenuEntry;
  litHref: string | null;
  panelOpen: boolean;
  activeModule: string | null;
  onPickModule: (id: string, alreadyOpen: boolean) => void;
  onPickLeaf: () => void;
}

function RailButton({
  entry,
  litHref,
  panelOpen,
  activeModule,
  onPickModule,
  onPickLeaf,
}: RailButtonProps) {
  const Icon = entry.icon;
  const isLeaf = entry.type === 'item';

  /* Three booleans, deliberately NOT collapsed into one (M1's lighting rule):
       isOpen      — this module's panel is showing
       holdsActive — the current ROUTE lives inside this module
       lit         — either of the above; paints the pill and claims aria-pressed
     Only `holdsActive` draws the ring bar. A module whose panel you opened while standing
     somewhere else is highlighted but does NOT claim to be where you are. */
  const holdsActive = isLeaf
    ? litHref !== null && litHref === entry.href
    : litHref !== null && moduleHrefs(entry).includes(litHref);
  const isOpen = !isLeaf && panelOpen && activeModule === entry.id;
  const lit = isOpen || holdsActive;

  const badge = moduleBadge(entry);
  // The design appends "— shared" for tagged (cross-department) modules.
  const tip = entry.type === 'group' && entry.tag ? `${entry.label} — shared` : entry.label;
  // The badge itself is aria-hidden, so the count has to reach assistive tech through the name.
  const accessibleName =
    badge > 0 ? `${tip} — ${badge} ${entry.railBadgeNoun ?? 'items need attention'}` : tip;

  const className = cn(
    'relative flex h-11 w-14 flex-shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
    lit
      ? 'bg-sidebar-active text-sidebar-active-foreground'
      : // NOT `text-muted-foreground`, which the design used: that is 2.60:1 on the Industrial
        // charcoal panel. This token passes in all three themes and matches the existing
        // sidebar precedent.
        'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
  );

  const inner = (
    <>
      {holdsActive && (
        <span
          aria-hidden="true"
          className="absolute -left-1.5 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-ring"
        />
      )}
      <Icon className="h-5 w-5" aria-hidden="true" />
      {/* `tracking-tight` is load-bearing, not styling: at the default tracking "What's New"
          measures 57px against a 56px box and ellipsises to "What's N…" — by ONE pixel. The design
          asks for +0.01em here, which makes it worse. Measured in-browser 2026-08-26. */}
      <span className="w-full truncate px-0.5 text-center text-2xs font-semibold leading-none tracking-tight">
        {entry.short ?? entry.label}
      </span>
      {badge > 0 && (
        // Anchored to the BUTTON's corner, not to the icon wrapper as the design does — anchored
        // to the 19px icon it grows leftward straight across the glyph it is annotating. Clamping
        // at 99+ (the `SidebarItem` precedent) caps it at three characters, and the sidebar-coloured
        // ring keeps it reading as a separate object rather than part of the icon.
        <span
          aria-hidden="true"
          className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-capitol-red px-1 text-2xs font-semibold text-white ring-2 ring-sidebar"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </>
  );

  return (
    <>
      {entry.dividerBefore && (
        <div aria-hidden="true" className="my-1.5 h-px w-8 flex-shrink-0 bg-sidebar-border" />
      )}
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {isLeaf ? (
            /* `Link`, deliberately NOT `NavLink`: NavLink brings its OWN active matcher, which
               prefix-matches and would therefore mark Home `aria-current="page"` on every route —
               the very bug NAV_ROUTE_MATCHERS exists to kill. One matcher, ours. A link is not a
               toggle, so it carries no aria-pressed at all. */
            <Link
              to={entry.href}
              aria-current={holdsActive ? 'page' : undefined}
              aria-label={accessibleName}
              onClick={onPickLeaf}
              className={className}
            >
              {inner}
            </Link>
          ) : (
            /* aria-pressed tracks the PANEL TOGGLE (isOpen), not `lit`. Using `lit` puts
               aria-pressed="true" on two buttons at once whenever the open panel belongs to a
               different module than the current route — the ambiguity M1 set out to remove. Where
               the user *is* is a separate axis, and that is what aria-current carries. */
            <button
              type="button"
              aria-pressed={isOpen}
              aria-current={holdsActive ? 'true' : undefined}
              aria-label={accessibleName}
              onClick={() => onPickModule(entry.id, isOpen)}
              className={className}
            >
              {inner}
            </button>
          )}
        </TooltipTrigger>
        {/* Portalled by Radix, so it is never clipped by the rail's overflow — the prototype's
            CSS ::after tooltip could not survive that and is not ported. */}
        <TooltipContent side="right" className="font-medium">
          {accessibleName}
        </TooltipContent>
      </Tooltip>
    </>
  );
}
